import { Fragment } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';

import { QUESTION_TYPES } from '@api/instant_survey';
import { Button, ModalV2 as Modal, truncateTextWithEllipsis } from '@utilities';

import QuestionLogicCondition from './components/QuestionLogicCondition';

import styles from './_index.module.scss';

const ModalLogicChangeConfirmation = ({
  onClose,
  questionBeingMoved,
  questionLogic,
  questions,
  updateSurvey,
}) => {
  const applyChanges = () => {
    updateSurvey({ questionLogic: proposedQuestionLogic, questions });
    onClose();
  };

  const affectedDisqualifyQuestions = questions.filter(
    ({ id }, index) => index > 1 && questionLogic[id]?.disqualify
  );
  const affectedSkipQuestions = questions.filter(({ id }, index) => {
    const currentLogic = questionLogic[id];
    if (!currentLogic?.skip) return false;

    if (
      currentLogic?.skip?.logic?.some(
        ({ question }) =>
          question &&
          questions.findIndex(({ id: questionId }) => question.id === questionId) > index
      )
    ) {
      return true;
    }

    return false;
  });

  const destinationIndex = questions
    .filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
    .findIndex(({ id }) => id === questionBeingMoved.id);

  const proposedQuestionLogic = cloneDeep(questionLogic);
  affectedDisqualifyQuestions.forEach((question) => {
    if (!proposedQuestionLogic[question.id]?.skip) {
      delete proposedQuestionLogic[question.id];
    } else {
      delete proposedQuestionLogic[question.id].disqualify;
    }
  });
  affectedSkipQuestions.forEach((question) => {
    // Only keep quota group logic and question logic that with questions above the current question
    proposedQuestionLogic[question.id].skip.logic = proposedQuestionLogic[
      question.id
    ].skip.logic.filter(
      (logic) =>
        !logic.question || questions.findIndex((q) => q.id === logic.question.id) < destinationIndex
    );
  });

  const actionButtons = [
    <Button key="modal-cancel-button" onClick={onClose} text="Cancel" variant="secondary" />,
    <Button key="modal-confirm-button" onClick={applyChanges} text="Save" />,
  ];

  return (
    <Modal
      buttons={actionButtons}
      id="modal-logic-change-confirmation"
      isActive
      onClose={onClose}
      size="large"
      title="Confirm Changes"
    >
      <div className={styles['modal-logic-change-confirmation-body']}>
        <p>
          Are you sure you want to move the question to{' '}
          <strong>position {destinationIndex + 1}</strong>?
        </p>

        {affectedSkipQuestions.map((question) => (
          <Fragment key={`affected-logic-${question.id}`}>
            <p>
              The following skip logic for{' '}
              <strong>“{truncateTextWithEllipsis({ length: 30, text: question.text })}”</strong>{' '}
              will be removed as it will invalidate your survey logic:
            </p>
            <QuestionLogicCondition
              question={question}
              questionBeingMoved={questionBeingMoved}
              questionLogic={questionLogic}
              selectedQuestions={questions}
              type="skip"
            />
          </Fragment>
        ))}
        {affectedDisqualifyQuestions.map((question) => (
          <Fragment key={`affected-logic-${question.id}`}>
            <p>
              The following for{' '}
              <strong>“{truncateTextWithEllipsis({ length: 30, text: question.text })}”</strong>{' '}
              will be removed as disqualification can only be applied at position 1 or 2 in your
              questionnaire:
            </p>
            <QuestionLogicCondition
              question={question}
              questionLogic={questionLogic}
              type="disqualify"
            />
          </Fragment>
        ))}
      </div>
    </Modal>
  );
};

ModalLogicChangeConfirmation.propTypes = {
  onClose: PropTypes.func.isRequired,
  questionBeingMoved: PropTypes.object.isRequired,
  questionLogic: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  updateSurvey: PropTypes.func.isRequired,
};

export default ModalLogicChangeConfirmation;
